/**
 * CallLogs — dense operator table over GET /api/calls (§13/§14).
 *
 * Mounted at both /calls (all calls, with an agent filter) and
 * /agents/:id/calls (scoped to one agent). Filters live in the URL so a
 * filtered view can be shared/bookmarked; pagination is server-side.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { api, ApiError } from "../lib/api";
import { formatDateTime, formatDuration, formatCost, formatMs } from "../lib/format";
import type {
  AgentSummary,
  CallDirection,
  CallListFilters,
  CallStatus,
  CallSummary,
  Paginated,
} from "../lib/types";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { Input } from "../components/Input";
import { Select } from "../components/Select";
import { Spinner } from "../components/Spinner";
import { Table, TBody, TD, TH, THead, TR } from "../components/Table";
import { CallStatusCell, DirectionBadge } from "../components/console/callDisplay";

const PAGE_SIZE = 25;

const DIRECTIONS: Array<{ value: CallDirection; label: string }> = [
  { value: "inbound", label: "Inbound" },
  { value: "outbound", label: "Outbound" },
  { value: "browser_test", label: "Browser test" },
];

const STATUSES: Array<{ value: CallStatus; label: string }> = [
  { value: "initiating", label: "Initiating" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "no_answer", label: "No answer" },
];

function parseDirection(value: string | null): CallDirection | undefined {
  return DIRECTIONS.some((d) => d.value === value) ? (value as CallDirection) : undefined;
}

function parseStatus(value: string | null): CallStatus | undefined {
  return STATUSES.some((s) => s.value === value) ? (value as CallStatus) : undefined;
}

function fromToCell(call: CallSummary): string {
  if (call.direction === "browser_test") return "browser";
  const from = call.from_number ?? "—";
  const to = call.to_number ?? "—";
  return `${from} → ${to}`;
}

export default function CallLogs() {
  const { id: agentIdParam } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [data, setData] = useState<Paginated<CallSummary> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const page = Math.max(1, Number.parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const filters = useMemo<CallListFilters>(
    () => ({
      agent_id: agentIdParam ?? searchParams.get("agent_id") ?? undefined,
      direction: parseDirection(searchParams.get("direction")),
      status: parseStatus(searchParams.get("status")),
      from: searchParams.get("from") ?? undefined,
      to: searchParams.get("to") ?? undefined,
      page,
      page_size: PAGE_SIZE,
    }),
    [agentIdParam, searchParams, page],
  );

  // Agent names for the filter select + the Agent column.
  useEffect(() => {
    const controller = new AbortController();
    api.agents
      .list(controller.signal)
      .then(setAgents)
      .catch(() => undefined); // non-fatal — the table still works with raw IDs
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api.calls
      .list(filters, controller.signal)
      .then((result) => {
        setData(result);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "Could not load calls.");
        setLoading(false);
      });
    return () => controller.abort();
  }, [filters, refreshTick]);

  const agentNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const agent of agents) map.set(agent.id, agent.name);
    return map;
  }, [agents]);

  const scopedAgent = agentIdParam ? (agentNames.get(agentIdParam) ?? "Agent") : null;

  function setFilter(key: string, value: string): void {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("page"); // any filter change resets pagination
    setSearchParams(next, { replace: true });
  }

  function setPage(nextPage: number): void {
    const next = new URLSearchParams(searchParams);
    if (nextPage > 1) next.set("page", String(nextPage));
    else next.delete("page");
    setSearchParams(next);
  }

  const hasFilters = Boolean(
    filters.direction || filters.status || filters.from || filters.to ||
      (!agentIdParam && filters.agent_id),
  );
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          {scopedAgent && agentIdParam && (
            <Link
              to={`/agents/${agentIdParam}`}
              className="text-xs text-muted transition-colors hover:text-bright"
            >
              ← {scopedAgent}
            </Link>
          )}
          <h1 className="font-display text-xl tracking-display text-bright">
            {scopedAgent ? `${scopedAgent} — calls` : "Calls"}
          </h1>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setRefreshTick((t) => t + 1)}>
          Refresh
        </Button>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-2">
        {!agentIdParam && (
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">Agent</span>
            <Select
              value={filters.agent_id ?? ""}
              onChange={(e) => setFilter("agent_id", e.target.value)}
              className="w-44"
            >
              <option value="">All agents</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name}
                </option>
              ))}
            </Select>
          </label>
        )}
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted">Direction</span>
          <Select
            value={filters.direction ?? ""}
            onChange={(e) => setFilter("direction", e.target.value)}
            className="w-36"
          >
            <option value="">All</option>
            {DIRECTIONS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </Select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted">Status</span>
          <Select
            value={filters.status ?? ""}
            onChange={(e) => setFilter("status", e.target.value)}
            className="w-36"
          >
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted">From date</span>
          <Input
            type="date"
            value={filters.from ?? ""}
            onChange={(e) => setFilter("from", e.target.value)}
            className="w-40"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted">To date</span>
          <Input
            type="date"
            value={filters.to ?? ""}
            onChange={(e) => setFilter("to", e.target.value)}
            className="w-40"
          />
        </label>
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="mb-1"
            onClick={() => setSearchParams(new URLSearchParams(), { replace: true })}
          >
            Clear filters
          </Button>
        )}
      </div>

      {error && (
        <p role="alert" className="rounded border border-danger/60 bg-surface px-3 py-2 text-sm text-danger">
          {error}
        </p>
      )}

      {loading && !data ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : data && data.items.length === 0 ? (
        <EmptyState
          title={hasFilters ? "No calls match these filters" : "No calls yet"}
          description={
            hasFilters
              ? "Widen the date range or clear a filter to see more."
              : "Test an agent from its editor or buy a number to start taking calls."
          }
          action={
            hasFilters ? (
              <Button onClick={() => setSearchParams(new URLSearchParams(), { replace: true })}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      ) : data ? (
        <>
          <Table>
            <THead>
              <TR>
                <TH>Started</TH>
                <TH>Agent</TH>
                <TH>Direction</TH>
                <TH>From → to</TH>
                <TH align="right">Duration</TH>
                <TH>Status</TH>
                <TH align="right">Latency p95</TH>
                <TH align="right">Cost</TH>
              </TR>
            </THead>
            <TBody>
              {data.items.map((call) => (
                <TR
                  key={call.id}
                  interactive
                  onClick={() => navigate(`/calls/${call.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") navigate(`/calls/${call.id}`);
                  }}
                  tabIndex={0}
                  aria-label={`Open call from ${formatDateTime(call.started_at)}`}
                >
                  <TD mono>{formatDateTime(call.started_at)}</TD>
                  <TD className="max-w-[180px] truncate">
                    {agentNames.get(call.agent_id) ?? "—"}
                  </TD>
                  <TD>
                    <DirectionBadge direction={call.direction} />
                  </TD>
                  <TD mono className="max-w-[240px] truncate">
                    {fromToCell(call)}
                  </TD>
                  <TD align="right" mono>
                    {formatDuration(call.duration_seconds)}
                  </TD>
                  <TD>
                    <CallStatusCell status={call.status} />
                  </TD>
                  <TD align="right" mono>
                    {formatMs(call.metrics?.voice_to_voice?.p95_ms)}
                  </TD>
                  <TD align="right" mono>
                    {formatCost(call.cost_estimate)}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted">
              {data.total === 0
                ? "0 calls"
                : `${(data.page - 1) * data.page_size + 1}–${Math.min(
                    data.page * data.page_size,
                    data.total,
                  )} of ${data.total} calls`}
            </p>
            <div className="flex items-center gap-2">
              <Button size="sm" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <span className="font-mono text-xs text-muted">
                {page} / {totalPages}
              </span>
              <Button
                size="sm"
                disabled={page >= totalPages || loading}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
